import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useLayoutEffect, useCallback, useRef, useState } from 'react';
import { useServiceWorkerUpdates } from './hooks/useServiceWorkerUpdates';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { RoleProvider } from './contexts/RoleContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import { PublicLayout } from './components/layout/PublicLayout';
import { AdminLayout } from './components/layout/AdminLayout';
import { ClientLayout } from './components/layout/ClientLayout';
import { MobileNav } from './components/layout/MobileNav';
import { OnboardingModal, MobileOnboarding, hasMobileOnboardingCompleted, StripeConnectOnboardingGate } from './components/onboarding';
import { AgeGateModal } from './components/AgeGateModal';
import { KeyboardDismissButton } from './components/ui';
import { isNative, isStandalone } from './lib/platform';
import { CookieConsent } from './components/CookieConsent';
import { CookieConsentProvider } from './contexts/CookieConsentContext';
import { ParentalControlsProvider } from './contexts/ParentalControlsContext';
import { SiteAdvertisingProvider } from './contexts/SiteAdvertisingContext';
import { RequireTeamUsernameModal } from './components/layout/RequireTeamUsernameModal';
import { useBackButton } from './hooks/usePlatform';
import { useMobileAppShell } from './hooks/useIsMobile';
import { shouldShowMobileNav } from './lib/mobileNavVisibility';

import {
  HomePage,
  TournamentsPage,
  TournamentDetailPage,
  TournamentRegistrationWrapper,
  MyTeamPage,
  AthleteBackendProfilePage,
  TeamsPage,
  TeamProfilePage,
  TeamBlogPostPage,
  TeamCalendarEventPage,
  TeamStorePage,
  TeamRegistrationPage,
  UserProfilePage,
  TournamentInvitesPage,
  RegisterAthletesPage,
  BracketPage,
  LiveMatchPage,
  LiveStreamsPage,
  ClientDashboardPage,
  InviteRegisterPage,
  TeamPaymentsPage,
  TeamBillingPage,
  BillingPage,
  JoinTeamPage,
  MediaUploadPage,
  PublicMediaAlbumPage,
  EmbedTeamFeedPage,
  EmbedContactFormPage,
  AthleteDashboardPage,
  LoginPage,
  RegisterPage,
  DashboardPage,
  TournamentsListPage,
  CreateTournamentPage,
  AdminTournamentDetailPage,
  OrganizationsPage,
  DuplicateTeamsPage,
  SchedulePage,
  SettingsPage,
  InviteLinksPage,
  QRCodesPage,
  InvoicesPage,
  StreamControlPage,
  EventPagesPage,
  EventPageEditPage,
  TournamentContactInquiriesPage,
  EventPageContactInquiriesPage,
  BoutSheetsPage,
  TournamentOperationsPage,
  ScoreboardControlPage,
  SampleMatchesPage,
  SampleEventsPage,
  PrintCenterPage,
  BracketManagementPage,
  BracketViewPage,
  ScoreboardDisplayPage,
  StreamOverlayPage,
  SiteAdminLoginPage,
  SiteAdminDashboardPage,
  SiteAdminChecklistPage,
  SiteAdminAdvertisingPage,
  EventPage,
  PricingPage,
  ForTeamsPage,
  NotificationPreferencesPage,
  NotificationsPage,
  EnhancedSearchPage,
  FeedPage,
  DiscoverPage,
  AthleteProfilePage,
  AthletesPage,
  LeaguePage,
  LeagueNewsPostPage,
  LeaguesPage,
  ClinicPage,
  ClinicsPage,
  LeaguesListPage,
  CreateLeaguePage,
  LeagueDetailPage,
  ClinicsListPage,
  CreateClinicPage,
  ClinicDetailPage,
  HowToPage,
  VolunteerManagementPage,
  ReportsPage,
  LeagueManagerDashboard,
  TeamManagerDashboard,
  TableWorkerDashboard,
  StaffScoreboardControlPage,
  StaffLoginPage,
  ParentFollowPage,
  VideoVaultPage,
  CoachFeesPage,
  CreateInvoicePage,
  TermsPage,
  PrivacyPolicyPage,
  DataDeletionPage,
  AboutPage,
  HelpPage,
  TutorialsPage,
  ContactPage,
  SupportedSportsPage,
  VolunteerSignupPage,
  TeamVolunteerHubPage,
  VolunteerInviteAcceptPage,
  TournamentRoleInviteAcceptPage,
  UnsubscribePage,
  NewsletterUnsubscribePage,
  EmergencyFormPage,
  EmergencyFormPreviewPage,
  RegistrationFormPreviewPage,
  MatchListDisplayPage,
  ClubBoutFeedPage,
  MatCenterPage,
  ManageTournamentRosterPage,
  AthleteEventSignupPage,
  TeamEventSignupPage,
  AthleteCheckInPage,
  AthleteCardPage,
  AthleteCheckInManagementPage,
  AdminTournamentTeamDetailPage,
  SetupLivePage,
  WrestlingTournamentGuidePage,
  StripePaymentsPage,
  TeamFeedPage,
  TeamChatPage,
  TeamInquiriesPage,
  FundraiserPage,
  FamilyHubPage,
  RefereeTimeclockAdminPage,
  RefereeTimeclockPage,
  TeamLeaderboardPage,
  GamificationScanPage,
  PracticeCheckInPage,
  MyCalendarPage,
  CommunicationsHubPage,
  MyHistoryPage,
  SocialCalendarPage,
  SocialOAuthCallbackPage,
  StripeConnectOAuthCallbackPage,
  QuickBooksAccountingCallbackPage,
  ForgotPasswordPage,
  NotFoundPage,
} from './lazyPages';

// Reset window scroll on route change so SPA navigations open at the top (not mid-page/footer).
function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useLayoutEffect(() => {
    if (hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [pathname, hash]);

  return null;
}

// Component to handle back button navigation
function BackButtonHandler() {
  const navigate = useNavigate();
  const location = useLocation();

  const handleBackButton = useCallback(() => {
    // If we're on the home page, let the default behavior (exit app) happen
    if (location.pathname === '/') {
      return false;
    }
    
    // Otherwise, navigate back
    navigate(-1);
    return true;
  }, [navigate, location.pathname]);

  useBackButton(handleBackButton);

  return null;
}

// Persists the last-visited route and restores it on cold start.
// Only active in native apps and installed PWAs, where the web shell
// always cold-starts at "/" and would otherwise lose the user's context.
const LAST_ROUTE_KEY = 'podium_last_route';
const ROUTE_PERSIST_SKIP = [
  '/',
  '/login',
  '/register',
  '/forgot-password',
  '/invite',
  '/logout',
];

function shouldPersistPath(pathname: string): boolean {
  if (ROUTE_PERSIST_SKIP.includes(pathname)) return false;
  // Skip any path that should always be opened directly (public share links,
  // OAuth callbacks, native-only standalone views).
  if (pathname.startsWith('/social-callback')) return false;
  if (pathname.startsWith('/accounting/')) return false;
  if (pathname.startsWith('/unsubscribe')) return false;
  if (pathname.startsWith('/newsletter/unsubscribe')) return false;
  if (pathname.startsWith('/follow/')) return false;
  if (pathname.startsWith('/tournament-signup/')) return false;
  if (pathname.startsWith('/team-signup/')) return false;
  if (pathname.startsWith('/volunteer/invite/')) return false;
  if (pathname.startsWith('/check-in/')) return false;
  if (pathname.startsWith('/staff/')) return false;
  if (pathname.startsWith('/scoreboard/')) return false;
  if (pathname.startsWith('/overlay/')) return false;
  return true;
}

function RouteRestorer() {
  const location = useLocation();
  const navigate = useNavigate();
  const { loading, isAuthenticated } = useAuth();
  const hasRestoredRef = useRef(false);

  // Only activate on native apps / installed PWAs.
  // Browser tabs already preserve the URL on hard reload natively.
  const shouldPersist = isNative() || (typeof window !== 'undefined' && isStandalone());

  // Restore last route on first load if we landed at "/" after a cold start.
  useEffect(() => {
    if (!shouldPersist) return;
    if (hasRestoredRef.current) return;
    if (loading) return;
    if (!isAuthenticated) {
      hasRestoredRef.current = true;
      return;
    }
    if (location.pathname !== '/') {
      hasRestoredRef.current = true;
      return;
    }
    try {
      const saved = localStorage.getItem(LAST_ROUTE_KEY);
      if (saved && saved !== '/' && saved.startsWith('/')) {
        hasRestoredRef.current = true;
        navigate(saved + (location.search || ''), { replace: true });
        return;
      }
    } catch {
      // Ignore storage errors.
    }
    hasRestoredRef.current = true;
    navigate('/feed' + (location.search || ''), { replace: true });
  }, [shouldPersist, loading, isAuthenticated, location.pathname, location.search, navigate]);

  // Save the current route as the user navigates so we can restore it later.
  useEffect(() => {
    if (!shouldPersist) return;
    if (!isAuthenticated) return;
    if (!shouldPersistPath(location.pathname)) return;
    try {
      localStorage.setItem(LAST_ROUTE_KEY, location.pathname);
    } catch {
      // Ignore storage errors.
    }
  }, [shouldPersist, isAuthenticated, location.pathname]);

  // Clear the saved route when the user signs out so we don't bounce a
  // returning guest into an authenticated route.
  useEffect(() => {
    if (!shouldPersist) return;
    if (isAuthenticated) return;
    if (loading) return;
    try {
      localStorage.removeItem(LAST_ROUTE_KEY);
    } catch {
      // Ignore storage errors.
    }
  }, [shouldPersist, isAuthenticated, loading]);

  return null;
}

// Main app content with routing
function AppContent() {
  const location = useLocation();
  const { isAuthenticated, needsOnboarding, loading, profile } = useAuth();
  const mobileAppShell = useMobileAppShell();
  const showMobileNav = shouldShowMobileNav(location.pathname, mobileAppShell);
  useServiceWorkerUpdates();
  
  // Track mobile onboarding state
  const [showMobileOnboarding, setShowMobileOnboarding] = useState(() => {
    // Only show on native platforms and if not already completed
    return isNative() && !hasMobileOnboardingCompleted();
  });

  // Don't show onboarding on auth pages or standalone site-admin routes
  const isAuthPage = location.pathname === '/login' || location.pathname === '/register' || location.pathname === '/invite' || location.pathname === '/forgot-password';
  const isSiteAdminStandaloneRoute = location.pathname.startsWith('/site-admin');
  const showOnboarding = !loading && isAuthenticated && needsOnboarding && !isAuthPage && !isSiteAdminStandaloneRoute;

  // Show mobile onboarding for first-time native app users
  if (showMobileOnboarding) {
    return <MobileOnboarding onComplete={() => setShowMobileOnboarding(false)} />;
  }

  return (
    <>
      <ScrollToTop />
      <BackButtonHandler />
      <RouteRestorer />
      
      {/* Onboarding modal for new users */}
      {showOnboarding && <OnboardingModal />}

      {/* Stripe Connect setup when entering coach or organizer role */}
      {!loading &&
        isAuthenticated &&
        !needsOnboarding &&
        !isAuthPage &&
        !isSiteAdminStandaloneRoute && <StripeConnectOnboardingGate />}

      {/* Age gate for users who haven't provided DOB yet */}
      {!loading &&
        isAuthenticated &&
        !needsOnboarding &&
        !isAuthPage &&
        !isSiteAdminStandaloneRoute &&
        profile &&
        !profile.date_of_birth && <AgeGateModal />}
      
      <div className={showMobileNav ? 'has-mobile-nav' : undefined}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/invite" element={<InviteRegisterPage />} />
        <Route path="/join-team" element={<JoinTeamPage />} />
        <Route path="/media-upload" element={<MediaUploadPage />} />
        <Route path="/media-albums/:shareCode" element={<PublicMediaAlbumPage />} />
        <Route path="/embed/team-feed" element={<EmbedTeamFeedPage />} />
        <Route path="/embed/contact" element={<EmbedContactFormPage />} />
        
        {/* Site Admin - Standalone (not part of regular admin) */}
        <Route path="/site-admin" element={<SiteAdminLoginPage />} />
        <Route path="/site-admin/dashboard" element={<SiteAdminDashboardPage />} />
        <Route path="/site-admin/checklist" element={<SiteAdminChecklistPage />} />
        <Route path="/site-admin/advertising" element={<SiteAdminAdvertisingPage />} />

        <Route element={<PublicLayout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/search" element={<EnhancedSearchPage />} />
          <Route path="/feed" element={<FeedPage />} />
          <Route path="/discover" element={<DiscoverPage />} />
          <Route path="/athletes" element={<AthletesPage />} />
          <Route path="/athletes/:athleteId" element={<AthleteProfilePage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/for-teams" element={<ForTeamsPage />} />
          <Route path="/tournaments" element={<TournamentsPage />} />
          <Route path="/tournaments/:slug" element={<TournamentDetailPage />} />
          <Route path="/tournaments/:slug/brackets/:bracketId" element={<BracketPage />} />
          <Route path="/tournaments/:id/volunteer" element={<VolunteerSignupPage />} />
          <Route path="/teams" element={<TeamsPage />} />
          <Route path="/fundraiser/:slug" element={<FundraiserPage />} />
          <Route path="/teams/:teamSlug/blog/:postSlug" element={<TeamBlogPostPage />} />
          <Route path="/teams/:teamSlug/calendar/:eventId" element={<TeamCalendarEventPage />} />
          <Route path="/teams/:teamSlug/events/:eventId/volunteer" element={<VolunteerSignupPage />} />
          <Route path="/teams/:teamSlug/volunteer" element={<TeamVolunteerHubPage />} />
          <Route path="/teams/:teamSlug/leaderboard" element={<TeamLeaderboardPage />} />
          <Route path="/teams/:teamSlug/gamification/scan" element={<GamificationScanPage />} />
          <Route path="/teams/:teamSlug/practice-check-in" element={<PracticeCheckInPage />} />
          <Route path="/teams/:teamSlug" element={<TeamProfilePage />} />
          <Route path="/teams/:teamSlug/store" element={<TeamStorePage />} />
          <Route path="/teams/:teamSlug/register" element={<TeamRegistrationPage />} />
          <Route path="/users/:userId" element={<UserProfilePage />} />
          <Route path="/leagues" element={<LeaguesPage />} />
          <Route path="/leagues/:slug/news/:postSlug" element={<LeagueNewsPostPage />} />
          <Route path="/leagues/:slug" element={<LeaguePage />} />
          <Route path="/clinics" element={<ClinicsPage />} />
          <Route path="/clinics/:slug" element={<ClinicPage />} />
          <Route path="/clinics/:id/volunteer" element={<VolunteerSignupPage />} />
          <Route path="/tutorials" element={<TutorialsPage />} />
        </Route>

        {/* Live Streaming Routes (standalone layout) */}
        <Route path="/live" element={<LiveStreamsPage />} />
        <Route path="/live/:matchId" element={<LiveMatchPage />} />

        {/* Legal Pages (standalone layout) */}
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/data-deletion" element={<DataDeletionPage />} />
        
        {/* Info Pages (standalone layout) */}
        <Route path="/about" element={<AboutPage />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/supported-sports" element={<SupportedSportsPage />} />
        <Route path="/setup-live" element={<SetupLivePage />} />
        <Route path="/wrestling-tournament-guide" element={<WrestlingTournamentGuidePage />} />
        <Route path="/stripe-payments" element={<StripePaymentsPage />} />

        {/* Event Pages (standalone layout - custom branded pages) */}
        <Route path="/events/:slug" element={<EventPage />} />

        {/* Scoreboard Display (standalone - for external screens) */}
        <Route path="/scoreboard/:matchId" element={<ScoreboardDisplayPage />} />

        {/* Stream Overlay (standalone - for OBS browser source) */}
        <Route path="/overlay/:matchId" element={<StreamOverlayPage />} />

        {/* Match List Display (standalone - for popup/external window) */}
        <Route path="/tournament/:id/match-list" element={<MatchListDisplayPage />} />
        <Route path="/tournament/:tournamentId/club/:teamId/bouts" element={<ClubBoutFeedPage />} />

        {/* MatCenter - public live view of all mats (standalone) */}
        <Route path="/tournament/:id/mat-center" element={<MatCenterPage />} />

        {/* Staff/Table Worker Routes (standalone - for mat workers) */}
        <Route path="/staff/:tournamentId/login" element={<StaffLoginPage />} />
        <Route path="/staff/:tournamentId" element={<TableWorkerDashboard />} />
        <Route path="/staff/:tournamentId/scoreboard/:matchId" element={<StaffScoreboardControlPage />} />
        <Route path="/referee/:tournamentId/timeclock" element={<RefereeTimeclockPage />} />

        {/* Parent Quick-Follow (standalone - no account needed) */}
        <Route path="/follow/:token" element={<ParentFollowPage />} />

        {/* Guardian Emergency Info Form (standalone - no account needed) */}
        <Route path="/emergency/:token" element={<EmergencyFormPage />} />
        <Route path="/emergency-preview/:token" element={<EmergencyFormPreviewPage />} />
        <Route path="/application-preview/:token" element={<RegistrationFormPreviewPage />} />

        {/* Athlete Self-Registration via share link (standalone - no auth needed) */}
        <Route path="/tournament-signup/:token" element={<AthleteEventSignupPage />} />

        {/* Team Event Signup via share link (standalone - no auth needed) */}
        <Route path="/team-signup/:token" element={<TeamEventSignupPage />} />

        {/* Athlete Self-Service Check-In (standalone - no auth needed, accessed via QR code) */}
        <Route path="/check-in/:tournamentId" element={<AthleteCheckInPage />} />

        {/* Public Athlete ID Card (QR scan target - optional auth for check-in) */}
        <Route path="/athlete-card/:participantId" element={<AthleteCardPage />} />

        {/* Volunteer Invite Acceptance (standalone) */}
        <Route path="/volunteer/invite/:token" element={<VolunteerInviteAcceptPage />} />

        {/* Tournament Role Invite Acceptance (standalone) */}
        <Route path="/tournament-role/invite/:token" element={<TournamentRoleInviteAcceptPage />} />

        {/* Social OAuth Callback (needs auth session to complete exchange) */}
        <Route path="/social-callback" element={<SocialOAuthCallbackPage />} />
        <Route path="/stripe-connect-callback" element={<StripeConnectOAuthCallbackPage />} />
        <Route
          path="/accounting/quickbooks/callback"
          element={<QuickBooksAccountingCallbackPage />}
        />

        {/* Notification Unsubscribe (standalone - no auth needed) */}
        <Route path="/unsubscribe" element={<UnsubscribePage />} />
        <Route path="/newsletter/unsubscribe/:token" element={<NewsletterUnsubscribePage />} />
        <Route path="/newsletter/unsubscribe" element={<NewsletterUnsubscribePage />} />

        {/* Tournament Registration (standalone - supports guest registration via invite) */}
        <Route path="/tournaments/:slug/register" element={<TournamentRegistrationWrapper />} />

        <Route element={<ClientLayout />}>
          <Route path="/dashboard" element={<ClientDashboardPage />} />
          <Route path="/athlete-dashboard" element={<AthleteDashboardPage />} />
          <Route path="/league-dashboard/:leagueId" element={<LeagueManagerDashboard />} />
          <Route path="/league-dashboard/:leagueId/team/:leagueTeamId" element={<TeamManagerDashboard />} />
          <Route path="/my-team" element={<MyTeamPage />} />
          <Route path="/my-team/submissions/emails" element={<MyTeamPage />} />
          <Route path="/my-team/submissions" element={<MyTeamPage />} />
          <Route path="/my-team/newsletter-subscribers" element={<MyTeamPage />} />
          <Route path="/my-team/events/:eventId/volunteers" element={<VolunteerManagementPage />} />
          <Route path="/my-team/athletes/:participantId" element={<AthleteBackendProfilePage />} />
          <Route path="/my-calendar" element={<MyCalendarPage />} />
          <Route path="/communications" element={<CommunicationsHubPage />} />
          <Route path="/my-history" element={<MyHistoryPage />} />
          <Route path="/team-feed/:teamId" element={<TeamFeedPage />} />
          <Route path="/team-chat/:teamId" element={<TeamChatPage />} />
          <Route path="/team-inquiries/:teamId" element={<TeamInquiriesPage />} />
          <Route path="/team-payments" element={<TeamPaymentsPage />} />
          <Route path="/team-billing" element={<TeamBillingPage />} />
          <Route path="/billing" element={<BillingPage />} />
          <Route path="/coach-fees" element={<CoachFeesPage />} />
          <Route path="/coach-fees/create-invoice" element={<CreateInvoicePage />} />
          <Route path="/video-vault" element={<VideoVaultPage />} />
          <Route path="/tournament-invites" element={<TournamentInvitesPage />} />
          <Route path="/tournaments/:slug/register-athletes" element={<RegisterAthletesPage />} />
          <Route path="/tournaments/:slug/manage-roster" element={<ManageTournamentRosterPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/notification-settings" element={<NotificationPreferencesPage />} />
          <Route path="/family" element={<FamilyHubPage />} />
          <Route path="/settings" element={<SettingsPage />} />
        </Route>

        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<DashboardPage />} />
          <Route path="tournaments" element={<TournamentsListPage />} />
          <Route path="tournaments/new" element={<CreateTournamentPage />} />
          <Route path="tournaments/:id" element={<AdminTournamentDetailPage />} />
          <Route path="tournaments/:id/operations" element={<TournamentOperationsPage />} />
          <Route path="tournaments/:id/bout-sheets" element={<BoutSheetsPage />} />
          <Route path="tournaments/:id/print" element={<PrintCenterPage />} />
          <Route path="tournaments/:tournamentId/divisions/:divisionId/brackets" element={<BracketManagementPage />} />
          <Route path="tournaments/:tournamentId/brackets/:bracketId" element={<BracketViewPage />} />
          <Route path="tournaments/:id/volunteers" element={<VolunteerManagementPage />} />
          <Route path="tournaments/:id/check-in" element={<AthleteCheckInManagementPage />} />
          <Route path="tournaments/:id/referee-timeclock" element={<RefereeTimeclockAdminPage />} />
          <Route path="tournaments/:id/teams/:teamId" element={<AdminTournamentTeamDetailPage />} />
          <Route path="leagues" element={<LeaguesListPage />} />
          <Route path="leagues/new" element={<CreateLeaguePage />} />
          <Route path="leagues/:id" element={<LeagueDetailPage />} />
          <Route path="clinics" element={<ClinicsListPage />} />
          <Route path="clinics/new" element={<CreateClinicPage />} />
          <Route path="clinics/:id" element={<ClinicDetailPage />} />
          <Route path="clinics/:id/volunteers" element={<VolunteerManagementPage />} />
          <Route path="invite-links" element={<InviteLinksPage />} />
          <Route path="qr-codes" element={<QRCodesPage />} />
          <Route path="invoices" element={<InvoicesPage />} />
          <Route path="event-pages" element={<EventPagesPage />} />
          <Route path="event-pages/:id/edit" element={<EventPageEditPage />} />
          <Route path="tournaments/:id/inquiries" element={<TournamentContactInquiriesPage />} />
          <Route path="event-pages/:id/inquiries" element={<EventPageContactInquiriesPage />} />
          <Route path="organizations" element={<OrganizationsPage />} />
          <Route path="duplicate-teams" element={<DuplicateTeamsPage />} />
          <Route path="schedule" element={<SchedulePage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="stream/:matchId" element={<StreamControlPage />} />
          <Route path="scoreboard/:matchId" element={<ScoreboardControlPage />} />
          <Route path="sample-events" element={<SampleEventsPage />} />
          <Route path="sample-matches" element={<SampleMatchesPage />} />
          <Route path="how-to" element={<HowToPage />} />
          <Route path="reports" element={<ReportsPage />} />
          <Route path="social-calendar" element={<SocialCalendarPage />} />
        </Route>

        {/* 404 catch-all */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </div>
      
      {/* Mobile bottom navigation */}
      <MobileNav />
      
      {/* Global keyboard dismiss button for mobile */}
      <KeyboardDismissButton />
      
      {/* Cookie consent banner + settings button */}
      <CookieConsent />

      {/* Blocks head coaches of legacy teams until they set a team username */}
      <RequireTeamUsernameModal />
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <CookieConsentProvider>
          <AuthProvider>
            <ThemeProvider>
              <RoleProvider>
                <ParentalControlsProvider>
                  <SiteAdvertisingProvider>
                    <AppContent />
                  </SiteAdvertisingProvider>
                </ParentalControlsProvider>
              </RoleProvider>
            </ThemeProvider>
          </AuthProvider>
        </CookieConsentProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
